import axios from 'axios';
import { useContext, useEffect } from 'react';
import { useHistory } from 'react-router';
import config from '../config';
import AuthContext from '../store/auth-context';

const api = axios.create({ baseURL: config.apiURL });

let lastPath = '/';

// Module-level refs to store current history and auth - the interceptor reads from these
let currentHistory = null;
let currentAuth = null;
let currentAccessTokenUrl = '/account/accessToken';
let currentRedirectURL = '/login';
let interceptorInitialized = false;

const refreshAccessToken = async (retryConfig) => {
  lastPath = currentHistory.location.pathname;

  // Get a new access token since old one is bad.
  const tokenFetcher = axios.create({ baseURL: config.apiURL, validateStatus: null });
  const response = await tokenFetcher.post(currentAccessTokenUrl);

  // Session Expired, redirect to login.
  if (response.status === 401) {
    currentHistory.push(`${currentRedirectURL}?redirect=${lastPath}`);
    currentAuth.clearAccessToken();
    throw new Error(response.data);

    // Use new token to re-dispatch the request.
  } else if (response.status === 200) {
    const newToken = response.headers['x-access-token'];
    currentAuth.login(newToken, response.data.exp);
    retryConfig.headers['x-access-token'] = newToken;
    return axios(retryConfig);

    // Other system error when getting new token.
  } else {
    throw new Error(
      response?.data ?? `Login process failed with HTTP response code ${response.status}.`
    );
  }
};

// Set up the interceptor once, globally
const initializeInterceptor = () => {
  if (interceptorInitialized) return;
  interceptorInitialized = true;

  api.interceptors.response.use(
    (response) => {
      return response;
    },
    (error) => {
      if (error.response?.status === 401) {
        if (currentHistory && currentHistory.location.pathname !== currentRedirectURL) {
          return refreshAccessToken(error.response.config);
        } else {
          throw new Error(error.response?.data ?? 'Unable to log in to the requested account.');
        }
      } else {
        throw error;
      }
    }
  );
};

export const useAPI = ({
  accessTokenUrl = '/account/accessToken',
  redirectURL = '/login',
  context = AuthContext,
} = {}) => {
  const history = useHistory();
  const auth = useContext(context);

  // Update module-level refs so the interceptor has access to current values
  currentHistory = history;
  currentAuth = auth;
  currentAccessTokenUrl = accessTokenUrl;
  currentRedirectURL = redirectURL;

  api.defaults.headers.common['x-access-token'] = auth.token;

  // Initialize the interceptor only once
  useEffect(() => {
    initializeInterceptor();
  }, []);

  return api;
};

// Only to be used where both the auth and router
// contexts are not available.
export const rawAPI = axios.create({ baseURL: config.apiURL });
